import { t } from "i18next";
import type { IconType } from "../../icon";
import type { ExamStatus } from "../../types";
import type { ButtonProps } from "../Button";
import type { TasksSectionItem } from "./types";

export const getStatRows = <T>(stats: T[]): T[][] =>
	stats.length === 4 ? [stats.slice(0, 2), stats.slice(2)] : [stats];

const TWO_COLUMN_MIN_WIDTH = 720;
const DESKTOP_CARDS_TO_SHOW = 2.15;

export const computeTaskCardWidth = (
	availableWidth: number,
	gap: number,
	totalCards: number,
): number => {
	// Desktop peeks at a quarter of the next card when there are enough to
	// scroll; with one or two tasks, size as two columns so a lone card never
	// spans the full row.
	if (availableWidth >= TWO_COLUMN_MIN_WIDTH) {
		const cardsToShow =
			totalCards > 2 ? DESKTOP_CARDS_TO_SHOW : Math.max(totalCards, 2);
		return (availableWidth - gap * (cardsToShow - 1)) / cardsToShow;
	}
	const cardsToShow = Math.min(availableWidth < 480 ? 1.1 : 1.5, totalCards);
	return (availableWidth - gap * (cardsToShow - 1)) / cardsToShow;
};

const COMPACT_CHIPS_MAX_CARD_WIDTH = 480;

/**
 * Swaps chips to their `compactLabel` on narrower cards so the footer has a
 * better chance of keeping chips and CTA on one line.
 */
export const shouldUseCompactChips = (
	cardWidth: number | undefined,
	isMobile: boolean,
): boolean =>
	cardWidth === undefined ? isMobile : cardWidth < COMPACT_CHIPS_MAX_CARD_WIDTH;

export const getButtonIconAndLabel = (
	status: ExamStatus,
	kind?: TasksSectionItem["kind"],
): { icon?: IconType; label: string } | null => {
	if (kind === "session" && status !== "COMPLETE") {
		return {
			icon: "play-circle-outline",
			label: t("dashboard.recommendedTasks.startSessionAction"),
		};
	}

	switch (status) {
		case "ACTIVE":
			return {
				icon: "play-pause",
				label: t("dashboard.recommendedTasks.resumeAction"),
			};
		case "COMPLETE":
			return {
				icon: "chart-box-outline",
				label: t("dashboard.recommendedTasks.reviewResultsAction"),
			};
		case "NOT_STARTED":
			return {
				icon: "play-circle-outline",
				label: t("dashboard.recommendedTasks.startTestAction"),
			};
		default:
			return null;
	}
};

/**
 * Mobile always uses `text` so the CTA never competes with the "Quick Study"
 * FAB. Desktop has no FAB, so emphasis follows status.
 */
export const getRecommendedCtaMode = (
	status: ExamStatus,
	isMobile: boolean,
): NonNullable<ButtonProps["mode"]> => {
	if (isMobile) {
		return "text";
	}
	switch (status) {
		case "NOT_STARTED":
			return "contained";
		case "ACTIVE":
			return "outlined";
		default:
			return "text";
	}
};
